import { CloseIcon } from '@components/Icons/CloseIcon';
import { SearchIcon } from '@components/Icons/SearchIcon';
import NapsterSpinner from '@components/NapsterSpinner';
import { FanItem, TagPickerWheel } from '@components/TagPickerWheel';
import { useStationMode } from '@hooks/station';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import { sendSelectCompanionMessage } from '@utils/station';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { translateFanPlaceholder } from '@utils/fanTranslations';
import { placeHolderData } from '../../config';
import {
  getAlbumById,
  getTrackById,
  quickSearch,
  SearchResult,
} from '../../services/api';
import { getTrackUrlForLocale } from '../../utils/localeUtils';

export interface SearchBarProps {
  placeholder?: string;
  className?: string;
  containerClassName?: string;
  dropdownClassName?: string;
  showAvatar?: boolean;
  onAvatarClick?: () => void;
  dropdownDirection?: 'up' | 'down';
  onDropdownStateChange?: (isOpen: boolean) => void;
  externalValue?: string;
  onExternalValueChange?: (value: string) => void;
  selectedTag?: string;
  onRemoveTag?: () => void;
  onGenerationSubmit?: (fullPrompt: string) => void;
  isGenerating?: boolean;
  capability?: string;
  selectedFanImage?: string;
  allFans?: FanItem[];
  selectedFanIndex?: number | null;
  showSearchDropdown: boolean;
  setShowSearchDropdown: (value: boolean) => void;
  selectedPlaceholder?: string;
  selectedContributorSlug: string | null;
}

const MAX_QUERY_LENGTH = 200;
const HINT_TIMEOUT = 3000;

export const SearchBar = memo(
  ({
    placeholder = 'Ask or search',
    className = '',
    containerClassName = '',
    dropdownClassName = '',
    showAvatar = false,
    onAvatarClick,
    dropdownDirection = 'down',
    onDropdownStateChange,
    externalValue,
    onExternalValueChange,
    selectedTag,
    onRemoveTag,
    onGenerationSubmit,
    isGenerating = false,
    capability,
    allFans = [],
    selectedFanIndex = null,
    showSearchDropdown = false,
    setShowSearchDropdown,
    selectedPlaceholder,
    selectedContributorSlug,
  }: SearchBarProps) => {
    const { t } = useTranslation();
    const { setActivePopup, setSelectedContributorSlug } = useCommonState();
    const { setActivePlaylist, addRecentTrack, preferredLocale } =
      useMusicState();
    const location = useLocation();
    const navigate = useNavigate();
    const isStationMode = useStationMode();
    const isOnSearchResultsPage = location.pathname.startsWith('/search');

    // Get capability-based placeholder when tag is selected.
    // Translate at render time so language switches reflect immediately.
    const getPlaceholder = () => {
      if (selectedTag && capability) {
        return selectedPlaceholder
          ? translateFanPlaceholder(selectedPlaceholder, t)
          : t('footer.typeYourPrompt');
      }
      return placeholder;
    };

    const effectivePlaceholder = getPlaceholder();

    // Search state
    const [internalSearchQuery, setInternalSearchQuery] = useState('');
    const searchQuery =
      externalValue !== undefined
        ? externalValue.slice(0, MAX_QUERY_LENGTH)
        : internalSearchQuery;
    const isAtMaxLength = searchQuery.length >= MAX_QUERY_LENGTH;
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [isSearchLoading, setIsSearchLoading] = useState(false);

    const [selectedSearchIndex, setSelectedSearchIndex] = useState(-1);
    const [isSearchFocused, setIsSearchFocused] = useState(false);

    const [showHint, setShowHint] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Refs
    const searchInputRef = useRef<HTMLInputElement>(null);
    const searchDropdownRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const searchAbortRef = useRef<AbortController | null>(null);
    const hintTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
      const mql = window.matchMedia('(max-width: 950px)');
      setIsMobile(mql.matches);
      const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }, []);

    useEffect(() => {
      if (selectedContributorSlug) {
        searchInputRef.current?.focus();

        if (!isMobile) {
          setShowHint(true);
          hintTimeoutRef.current = setTimeout(() => {
            setShowHint(false);
          }, HINT_TIMEOUT);
        }
      } else {
        setShowHint(false);
        if (hintTimeoutRef.current) {
          clearTimeout(hintTimeoutRef.current);
          hintTimeoutRef.current = null;
        }
      }
    }, [selectedContributorSlug, isMobile]);

    // Search functionality
    const performSearch = useCallback(
      async (query: string) => {
        // Don't perform search if a tag is selected (generation mode)
        if (selectedTag) {
          return;
        }

        if (isOnSearchResultsPage) {
          setSearchResults([]);
          setShowSearchDropdown(false);
          setSelectedSearchIndex(-1);
          setIsSearchLoading(false);
          return;
        }

        if (!query.trim()) {
          setSearchResults([]);
          setShowSearchDropdown(false);
          setIsSearchLoading(false);
          return;
        }

        // Cancel previous search request
        if (searchAbortRef.current) {
          searchAbortRef.current.abort();
        }
        searchAbortRef.current = new AbortController();

        setIsSearchLoading(true);
        try {
          const results = await quickSearch(query);
          // Only update results if this request wasn't aborted
          if (!searchAbortRef.current.signal.aborted) {
            setSearchResults(results);
            // Never show suggestion dropdown on search results page.
            setShowSearchDropdown(
              !isOnSearchResultsPage &&
                isSearchFocused &&
                query.trim().length > 0,
            );
            setSelectedSearchIndex(-1);
          }
        } catch (error) {
          // Ignore abort errors
          if (error instanceof Error && error.name !== 'AbortError') {
            console.error('Search error:', error);
            setSearchResults([]);
            setShowSearchDropdown(false);
          }
        } finally {
          setIsSearchLoading(false);
        }
      },
      [isOnSearchResultsPage, isSearchFocused, selectedTag],
    );

    // Handle search input change with debouncing
    const handleSearchInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.slice(0, MAX_QUERY_LENGTH);

        if (onExternalValueChange) {
          onExternalValueChange(value);
        } else {
          setInternalSearchQuery(value);
        }

        // Clear previous debounce
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }

        if (isOnSearchResultsPage) {
          setSearchResults([]);
          setShowSearchDropdown(false);
          setSelectedSearchIndex(-1);
          setIsSearchLoading(false);
          return;
        }

        // if tag is there, dont debounce
        if (!selectedTag) {
          // Set new debounce
          debounceRef.current = setTimeout(() => {
            performSearch(value);
          }, 600);
        }
      },
      [
        isOnSearchResultsPage,
        performSearch,
        onExternalValueChange,
        selectedTag,
      ],
    );

    // Handle search submit (Enter key)
    const handleSearchSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault();

        if (!searchQuery.trim()) return;

        // if we have a selected tag and onGenerationSubmit, trigger generation
        if (selectedTag && onGenerationSubmit) {
          const fullPrompt = searchQuery.trim();
          onGenerationSubmit(fullPrompt);
          return;
        }

        // othebwise, do normal search
        // Remove focus from search input to hide dropdown
        if (searchInputRef.current) {
          searchInputRef.current.blur();
        }

        const searchUrl = `/search?q=${encodeURIComponent(searchQuery.trim())}`;

        // Navigate to search results page first, then set active page
        navigate(searchUrl, {
          state: {
            from: `${location.pathname}${location.search}#search-input-search-bar`,
          },
        });
      },
      [
        searchQuery,
        navigate,
        selectedTag,
        onGenerationSubmit,
        location.pathname,
        location.search,
      ],
    );

    // Handle clear search
    const handleClearSearch = useCallback(() => {
      if (onExternalValueChange) {
        onExternalValueChange('');
      } else {
        setInternalSearchQuery('');
      }
      setSearchResults([]);
      setShowSearchDropdown(false);
      setIsSearchLoading(false);
      setSelectedSearchIndex(-1);
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [onExternalValueChange]);

    // Handle search input focus
    const handleSearchFocus = useCallback(() => {
      setIsSearchFocused(true);
      if (searchQuery.trim() && !selectedTag && !isOnSearchResultsPage) {
        setShowSearchDropdown(true);
      }
    }, [isOnSearchResultsPage, searchQuery, selectedTag]);

    useEffect(() => {
      if (isOnSearchResultsPage && showSearchDropdown) {
        setShowSearchDropdown(false);
        setSelectedSearchIndex(-1);
      }
    }, [isOnSearchResultsPage, showSearchDropdown, setShowSearchDropdown]);

    useEffect(() => {
      if (!isOnSearchResultsPage) return;
      setIsSearchLoading(false);
      setSearchResults([]);
      setShowSearchDropdown(false);
      setSelectedSearchIndex(-1);
    }, [isOnSearchResultsPage, setShowSearchDropdown]);

    // Handle search input blur
    const handleSearchBlur = useCallback(() => {
      setIsSearchFocused(false);
      setShowHint(false);
      // Hide dropdown after a small delay to allow for clicks on dropdown items
      setSelectedContributorSlug(null);
      setTimeout(() => {
        setShowSearchDropdown(false);
        setSelectedSearchIndex(-1);
      }, 150);
    }, []);

    // Handle search result click
    const handleSearchResultClick = useCallback(
      async (result: SearchResult) => {
        setShowSearchDropdown(false);
        if (onExternalValueChange) {
          onExternalValueChange('');
        } else {
          setInternalSearchQuery('');
        }

        try {
          if (result.type === 'Album') {
            // Play album - fetch full album data and start playing
            const fullAlbumData = await getAlbumById(result.id);
            if (
              fullAlbumData &&
              fullAlbumData.tracks &&
              fullAlbumData.tracks.length > 0
            ) {
              const contributor = fullAlbumData.contributors?.[0];
              const firstTrack = fullAlbumData.tracks[0];

              // Add first track to recent tracks
              if (contributor) {
                const recentTrack = {
                  track: {
                    name: firstTrack.name,
                    url: getTrackUrlForLocale(firstTrack, preferredLocale),
                    trackIndex: 1,
                  },
                  contributor: {
                    id: fullAlbumData.id,
                    givenName: contributor.givenName || 'Unknown Artist',
                    thumbnailUrl:
                      contributor.thumbnailUrl ||
                      placeHolderData.contributorThumbnail,
                  },
                  albumArtUrl:
                    fullAlbumData?.thumbnailUrl ||
                    fullAlbumData?.albumArtUrl ||
                    contributor?.thumbnailUrl ||
                    placeHolderData.albumCover,
                  timestamp: Date.now(),
                };
                addRecentTrack(recentTrack);
              }

              const playlist = {
                currentIndex: 0,
                album: fullAlbumData,
              };
              setActivePlaylist(playlist);
              setActivePopup('musicplayer');
            }
          } else if (result.type === 'Track') {
            // Play single track - create a single-track playlist
            const trackInfo = await getTrackById(result.id);
            if (trackInfo) {
              // Create a track object with the new locale-aware structure
              const trackWithUrls = {
                id: trackInfo.id,
                name: trackInfo.trackName,
                duration: 0, // Duration not available in favorite track data
                isLiked: trackInfo.isLiked ?? false,
                // Use the new urls structure from the API response, with fallback for backward compatibility
                urls: trackInfo.audioUrl?.urls || [],
                // Keep the old url property as fallback if urls array is empty
                url:
                  trackInfo.audioUrl?.urls && trackInfo.audioUrl.urls.length > 0
                    ? undefined
                    : trackInfo.audioUrl?.path +
                      '/' +
                      trackInfo.audioUrl?.fileName +
                      '.mp3',
              };

              // Create a single-track album for this favorite track
              const singleTrackAlbum = {
                id: trackInfo?.album?.id,
                albumName: trackInfo.album.name,
                albumArtUrl:
                  trackInfo?.album?.thumbnailUrl ||
                  trackInfo?.album?.albumArtUrl,
                contributors: trackInfo.contributors,
                tracks: [trackWithUrls],
                category: 'favorites',
                likes: 0,
                isLiked: trackInfo.isLiked ?? false,
                upc: '',
                pk: '',
                description: '',
                genre: [],
                type: 'search',
              };

              const playlist = {
                currentIndex: 0,
                album: singleTrackAlbum,
              };
              const contributor = trackInfo.contributors[0];
              // Add to recent tracks
              const recentTrack = {
                track: {
                  name: trackInfo.trackName,
                  url: getTrackUrlForLocale(trackWithUrls, preferredLocale),
                  trackIndex: 1,
                },
                contributor: {
                  id: contributor?.id,
                  givenName: contributor?.givenName || 'Unknown Artist',
                  thumbnailUrl:
                    contributor?.thumbnailUrl ||
                    placeHolderData.contributorThumbnail,
                },
                albumArtUrl:
                  trackInfo?.album?.thumbnailUrl ||
                  trackInfo?.album?.albumArtUrl ||
                  contributor?.thumbnailUrl ||
                  placeHolderData.albumCover,
                timestamp: Date.now(),
              };
              addRecentTrack(recentTrack);

              setActivePlaylist(playlist);
              setActivePopup('musicplayer');
            }
          } else if (result.type === 'Artist') {
            // Navigate to contributor profile
            navigate(`/contributor/${result.id}`);
          } else if (result.type === 'Mentor') {
            if (isStationMode) {
              sendSelectCompanionMessage(result.id);
              return;
            }
            // Navigate to mentor/companion page
            navigate(`/${result.slug}?start=true`);
          }
        } catch (error) {
          console.error('Error handling search result click:', error);
        }
      },
      [
        setActivePlaylist,
        setActivePopup,
        addRecentTrack,
        preferredLocale,
        navigate,
        onExternalValueChange,
        isStationMode,
      ],
    );

    // Handle keyboard navigation
    const handleSearchKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (!showSearchDropdown || searchResults.length === 0) {
          return;
        }

        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setSelectedSearchIndex((prev) =>
              prev < searchResults.length - 1 ? prev + 1 : prev,
            );
            break;
          case 'ArrowUp':
            e.preventDefault();
            setSelectedSearchIndex((prev) => (prev > 0 ? prev - 1 : prev));
            break;
          case 'Enter':
            e.preventDefault();
            if (
              selectedSearchIndex >= 0 &&
              selectedSearchIndex < searchResults.length
            ) {
              handleSearchResultClick(searchResults[selectedSearchIndex]);
            } else {
              handleSearchSubmit(e);
            }
            break;
          case 'Escape':
            setShowSearchDropdown(false);
            setSelectedSearchIndex(-1);
            if (searchInputRef.current) {
              searchInputRef.current.blur();
            }
            break;
        }
      },
      [
        showSearchDropdown,
        searchResults,
        selectedSearchIndex,
        handleSearchResultClick,
        handleSearchSubmit,
      ],
    );

    // Click outside to close dropdown
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          searchDropdownRef.current &&
          !searchDropdownRef.current.contains(event.target as Node) &&
          searchInputRef.current &&
          !searchInputRef.current.contains(event.target as Node)
        ) {
          setShowSearchDropdown(false);
          setSelectedSearchIndex(-1);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Notify parent component when dropdown state changes
    useEffect(() => {
      if (onDropdownStateChange) {
        onDropdownStateChange(showSearchDropdown);
      }
    }, [showSearchDropdown, onDropdownStateChange]);

    return (
      <div
        className={`relative ${
          showSearchDropdown ? 'z-[100]' : ''
        } ${containerClassName}`}>
        {/* Search Results Dropdown */}
        <div
          className={`flex flex-col p-2 w-full ${
            showSearchDropdown
              ? 'shadow-md bg-black/80 rounded-t-3xl rounded-b-[30px] backdrop-blur-md pointer-events-auto'
              : ''
          }`}>
          {showSearchDropdown && (
            <div
              ref={searchDropdownRef}
              className={`flex flex-col gap-2 scrollbar-tiny overflow-auto max-h-[40vh] pb-2 ${
                dropdownDirection === 'up' ? 'top-[50px]' : 'bottom-[50px]'
              }  ${dropdownClassName}`}>
              {searchResults.length > 0 ? (
                <>
                  {searchResults.map((result, index) => (
                    <div
                      key={`${result.type}-${result.id}`}
                      className={`flex gap-4 items-center p-2 tc-mv-blur cursor-pointer transition-colors duration-200 ${
                        index === selectedSearchIndex ? 'bg-white/10' : ''
                      } ${index === 0 ? 'rounded-t-lg' : ''} ${
                        index === searchResults.length - 1 ? 'rounded-b-lg' : ''
                      }`}
                      onClick={() => handleSearchResultClick(result)}>
                      {/* Thumbnail */}
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-white/10">
                        <img
                          src={
                            result.albumArtUrl ||
                            result.thumbnailUrl ||
                            placeHolderData.albumCover
                          }
                          alt={result.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              placeHolderData.albumCover;
                          }}
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-white font-medium text-sm truncate">
                            {result.name}
                          </h3>
                          <span
                            className={`px-2 py-0.5 text-xs rounded-full flex-shrink-0 ${
                              result.type === 'Album'
                                ? 'bg-blue-500/20 text-blue-300'
                                : result.type === 'Track'
                                  ? 'bg-green-500/20 text-green-300'
                                  : result.type === 'Mentor'
                                    ? 'bg-orange-500/20 text-orange-300'
                                    : 'bg-purple-500/20 text-purple-300'
                            }`}>
                            {result.type === 'Artist'
                              ? 'Contributor'
                              : result.type === 'Mentor'
                                ? 'Companion'
                                : result.type}
                          </span>
                        </div>

                        {/* Artist name */}
                        {result.contributors &&
                          result.contributors.length > 0 && (
                            <p className="text-white/60 text-xs truncate">
                              by {result.contributors[0].givenName}
                            </p>
                          )}

                        {/* Album name for tracks */}
                        {result.type === 'Track' && result.albumName && (
                          <p className="text-white/40 text-xs truncate">
                            from {result.albumName}
                          </p>
                        )}

                        {/* Job title for mentors */}
                        {result.type === 'Mentor' && result.jobTitle && (
                          <p className="text-white/60 text-xs text-left truncate">
                            {result.jobTitle}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* See all results footer */}
                  <div
                    className="flex gap-4 items-center justify-center p-2 tc-mv-blur cursor-pointer transition-colors duration-200 rounded-lg"
                    onClick={() =>
                      handleSearchSubmit({
                        preventDefault: () => {},
                      } as React.FormEvent)
                    }>
                    <div className="flex items-center justify-center w-full h-10 rounded-lg overflow-hidden  text-white/80 text-sm">
                      See all results for "{searchQuery}"
                    </div>
                  </div>
                </>
              ) : !isSearchLoading && searchQuery.trim() ? (
                /* No Results Message */
                <div className="p-6 text-center">
                  <div className="flex justify-center mb-4">
                    <SearchIcon />
                  </div>
                  <h3 className="text-white text-lg font-medium mb-2 font-[NapsterSans]">
                    No content found for "{searchQuery}"
                  </h3>
                  <p className="text-white/60 text-sm font-[NapsterSans]">
                    Please make sure your search is spelled correctly, or use
                    fewer or different keywords.
                  </p>
                </div>
              ) : null}
            </div>
          )}

          <form
            onSubmit={handleSearchSubmit}
            className={`flex w-full z-99 bg-white/65 border ${
              isAtMaxLength
                ? 'border-red-500 ring-2 ring-red-300/60'
                : 'border-white/50'
            } rounded-full py-2 pr-10 relative font-[NapsterSans] items-center gap-2 
          ${className} 
          ${selectedTag ? 'px-2' : 'px-4'}`}>
            {selectedTag && allFans.length > 0 && selectedFanIndex !== null && (
              <div className="flex items-center gap-1 bg-blue-100 text-[#040720] px-2 py-2 rounded-full text-sm font-medium whitespace-nowrap">
                <TagPickerWheel
                  fans={allFans}
                  selectedIndex={selectedFanIndex}
                />
                {onRemoveTag && (
                  <button
                    type="button"
                    onClick={onRemoveTag}
                    className="ml-1 hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                    aria-label="Remove fan">
                    <CloseIcon
                      width={16}
                      height={16}
                      fillOpacity={1}
                      fillColor="black"
                    />
                  </button>
                )}
              </div>
            )}

            <input
              id="search-input-search-bar"
              ref={searchInputRef}
              className={`search-input border-0 min-h-[36px] flex-1 min-w-0 outline-none bg-transparent overflow-hidden text-ellipsis ${
                isGenerating ? 'text-[#040720]/50' : 'text-[#040720]'
              }`}
              placeholder={effectivePlaceholder}
              name="prompt"
              type="text"
              value={searchQuery}
              onChange={handleSearchInputChange}
              onKeyDown={handleSearchKeyDown}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              autoComplete="off"
              disabled={isGenerating}
              maxLength={MAX_QUERY_LENGTH}
            />

            {/* Loading Spinner or Clear Button */}
            <div className="flex items-center absolute right-[16px] top-0 bottom-0">
              {isGenerating || (isSearchLoading && !isOnSearchResultsPage) ? (
                <NapsterSpinner size="small" />
              ) : (
                searchQuery && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="flex items-center justify-center w-6 h-6 rounded-full hover:bg-white/20 transition-colors duration-200">
                    <CloseIcon
                      width={16}
                      height={16}
                      fillOpacity={1}
                      fillColor={'black'}
                    />
                  </button>
                )
              )}
            </div>
          </form>
        </div>

        {showHint && (
          <div
            className="absolute z-50 left-full ml-2 top-1/2 -translate-y-1/2"
            role="tooltip">
            <div className="relative bg-linear-to-r from-blue-500 to-purple-600 text-white text-sm font-medium px-4 py-2.5 rounded-xl shadow-xl whitespace-nowrap">
              <div className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-[6px] border-b-[6px] border-r-[6px] border-t-transparent border-b-transparent border-r-blue-500" />
              Describe your song idea <br /> and let's make it.
            </div>
          </div>
        )}

        {/* Avatar button if needed */}
        {showAvatar && onAvatarClick && (
          <div className="flex ml-2">
            <button onClick={onAvatarClick}>
              <img
                className="flex w-[44px] h-[44px] min-w-[44px] max-w-full object-cover rounded-full"
                src="/assets/images/chat_avatar.png"
                alt="Companion"
              />
            </button>
          </div>
        )}
      </div>
    );
  },
);

SearchBar.displayName = 'SearchBar';

export default SearchBar;
